import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import {
  TariffCalculationParams,
  TariffCalculationResponse,
  useTariffCalculationMutation,
} from 'src/hooks/useTariffCalculation.mutation';

/** 件数 0 の項目は文言に出さない（V3Plans の流儀） */
function buildCountText({ billingUpdatedCount, paymentUpdatedCount, skippedCount }: TariffCalculationResponse): string {
  const parts: string[] = [];
  if (billingUpdatedCount > 0) parts.push(`請求: ${billingUpdatedCount}件`);
  if (paymentUpdatedCount > 0) parts.push(`支払: ${paymentUpdatedCount}件`);
  if (skippedCount > 0) parts.push(`スキップ: ${skippedCount}件`);
  return parts.join('、');
}

/**
 * 配車サマリからの運賃計算。/map とカード配車で同じ挙動にする。
 *
 * Why not V3Plans の既存ハンドラを共有: あちらは invalidate するキーが違う
 * (PLANNINGS_DELIVERIES_WITH_LEG_RUNS)。共有するとどちらかに無駄な再取得が出る。
 */
export function useTariffCalculationAction(): {
  execute: (params: TariffCalculationParams) => void;
  isExecuting: boolean;
} {
  const mutation = useTariffCalculationMutation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const execute = useCallback((params: TariffCalculationParams) => {
    mutation.mutate(params, {
      onSuccess: (data: TariffCalculationResponse) => {
        // 成功扱いになる件数の合計（更新・スキップいずれも成功）
        const resolvedCount = data.billingUpdatedCount + data.paymentUpdatedCount + data.skippedCount;
        // スキップは失敗ではない。更新 0 でもスキップがあれば処理は成功している
        if (data.errors.length > 0 && resolvedCount === 0) {
          enqueueSnackbar(`運賃計算に失敗しました。失敗: ${data.errors.length}件`, { variant: 'error' });
          return;
        }
        const counts = buildCountText(data);
        if (data.errors.length > 0) {
          enqueueSnackbar(`運賃計算が一部失敗しました。${counts}／失敗: ${data.errors.length}件`, { variant: 'warning' });
        } else {
          enqueueSnackbar(`運賃計算が完了しました。${counts}`, { variant: 'success' });
        }
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
      },
      onError: (error: unknown) => {
        const axiosError = error as { response?: { data?: { message?: string } } };
        enqueueSnackbar(axiosError?.response?.data?.message || '運賃計算に失敗しました。', { variant: 'error' });
      },
    });
  }, [mutation, queryClient, enqueueSnackbar]);

  return { execute, isExecuting: mutation.isPending };
}
